import { useCallback, useRef } from "react";
// Records made after a signed-in session started recording but before the client has loaded (the first
// screen view, an early click) wait here and are sent once it starts. Nothing waits before sign-in, so a
// record made then can never be sent under the user who signs in next.
const MAX_WAITING = 100;
let client;
let waiting;
function debugging() {
    try {
        return globalThis.localStorage?.getItem("nxtUsageDebug") != null;
    }
    catch {
        return false;
    }
}
function deliver(delivery, describe) {
    try {
        if (debugging())
            console.debug("[usage]", ...describe());
        if (client)
            delivery(client);
        else if (waiting && waiting.length < MAX_WAITING)
            waiting.push(delivery);
    }
    catch {
        // Usage recording never affects the app.
    }
}
export function startUsageClient(started) {
    client = started;
    const queued = waiting ?? [];
    waiting = undefined;
    for (const delivery of queued) {
        try {
            delivery(started);
        }
        catch {
            // Dropped.
        }
    }
}
/** A signed-in session records: keep what is recorded until the client has started. */
export function openUsageRecording() {
    if (!client && !waiting)
        waiting = [];
}
/** Nothing is recorded (no configuration, signed out, the client failed to load): drop the client and what waits. */
export function stopUsageRecording() {
    client = undefined;
    waiting = undefined;
}
export function resetUsageTrackingForTests() {
    client = undefined;
    waiting = undefined;
}
/** Records a usage event from the catalog. */
export function track(name, ...[props]) {
    const data = props ?? {};
    deliver((c) => c.recordEvent(name, data), () => [name, data]);
}
// Long enough for a signed request to leave, short enough not to be felt as a slow click.
const LEAVE_WAIT_MS = 300;
/**
 * Records an event whose action ends the page's recording, a full navigation or a sign-out, and settles once
 * the client has sent it, or after 300 ms. A flush on `pagehide` does not get a signed request out in time
 * (design D9). Continue the action once it settles.
 */
export function trackBeforeLeave(name, ...props) {
    track(name, ...props);
    const sending = client;
    if (!sending)
        return Promise.resolve();
    return new Promise((resolve) => {
        const timer = setTimeout(resolve, LEAVE_WAIT_MS);
        const settle = () => {
            clearTimeout(timer);
            resolve();
        };
        try {
            sending.flush().then(settle, settle);
        }
        catch {
            settle();
        }
    });
}
/**
 * Records a click on a link that leaves the page: a plain click follows the link once `trackBeforeLeave`
 * settles. A click that opens a new tab or window leaves nothing and is only recorded.
 */
export function trackLinkLeave(event, name, ...props) {
    const link = event.currentTarget.closest("a");
    const plain = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !event.defaultPrevented;
    if (!link?.href || !plain || (link.target !== "" && link.target !== "_self")) {
        track(name, ...props);
        return;
    }
    event.preventDefault();
    const href = link.href;
    void trackBeforeLeave(name, ...props).then(() => window.location.assign(href));
}
/**
 * The page id is the path: RUM ignores a view whose id equals the current page's, so two artifacts under one
 * route template would otherwise be one view, and later records would keep the first one's attributes. The
 * template is the `route` attribute, and the title too: the document title names user-authored content.
 */
export function recordScreenView(routeTemplate, path) {
    deliver((c) => c.recordPageView({ pageId: path, pageAttributes: { route: routeTemplate, title: routeTemplate } }), () => ["screen", routeTemplate, path]);
}
/** An error a React error boundary caught, which the client's own window handlers never see. */
export function reportUsageError(error) {
    deliver((c) => c.recordError(error), () => ["error", error]);
}
export function setUsageAttributes(attributes) {
    deliver((c) => c.addSessionAttributes(attributes), () => ["attributes", attributes]);
}
/**
 * Records a dialog's outcome: `.opened` when it opens, `.submitted` when its action succeeds (call
 * `markSubmitted` from the mutation's success), and `.dismissed` when it closes without a success during
 * that opening. Success paths that close the dialog themselves therefore record no `dismissed`.
 */
export function useTrackedDialog(name, props) {
    const submitted = useRef(false);
    const open = useRef(false);
    const latestProps = useRef(props);
    latestProps.current = props;
    const onOpenChange = useCallback((next) => {
        if (next && !open.current) {
            open.current = true;
            submitted.current = false;
            track(`${name}.opened`, { ...latestProps.current });
        }
        else if (!next && open.current) {
            open.current = false;
            if (!submitted.current)
                track(`${name}.dismissed`, { ...latestProps.current });
        }
    }, [name]);
    const markSubmitted = useCallback((submitProps) => {
        if (submitted.current)
            return;
        submitted.current = true;
        track(`${name}.submitted`, { ...latestProps.current, ...submitProps });
    }, [name]);
    return { onOpenChange, markSubmitted };
}
//# sourceMappingURL=track.js.map